export const CAMPAIGN_STATUSES = ['active', 'review', 'deactivated'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CAMPAIGN_TYPES = [
  'Product launch',
  'Brand awareness',
  'Seasonal push',
  'Retargeting',
] as const;
export type CampaignType = (typeof CAMPAIGN_TYPES)[number];

export type BudgetMode = 'daily' | 'total';
export type CampaignFocus = 'Product' | 'Service';

export interface Campaign {
  id: string;
  name: string;
  brand: string;
  type: string;
  status: CampaignStatus;
  updatedLabel: string;
  gradientIndex: number;
  description: string;
  goal: string;
  start: string;
  end: string;
  budget: string;
  budgetMode: BudgetMode;
  labels: string[];
  audience: string;
  ageGroups: string[];
  regions: string[];
  categories: string[];
  focus: CampaignFocus;
}

export interface CampaignFormValues {
  name: string;
  description: string;
  type: string;
  goal: string;
  start: string;
  end: string;
  budget: string;
  budgetMode: BudgetMode;
  labels: string[];
  brand: string;
  audience: string;
  ageGroups: string[];
  regions: string[];
}

export const EMPTY_CAMPAIGN_FORM: CampaignFormValues = {
  name: '',
  description: '',
  type: '',
  goal: '',
  start: '',
  end: '',
  budget: '',
  budgetMode: 'daily',
  labels: [],
  brand: '',
  audience: '',
  ageGroups: [],
  regions: [],
};

export function toFormValues(campaign: Campaign): CampaignFormValues {
  return {
    name: campaign.name,
    description: campaign.description,
    type: campaign.type,
    goal: campaign.goal,
    start: campaign.start,
    end: campaign.end,
    budget: campaign.budget,
    budgetMode: campaign.budgetMode,
    labels: [...campaign.labels],
    brand: campaign.brand,
    audience: campaign.audience,
    ageGroups: [...campaign.ageGroups],
    regions: [...campaign.regions],
  };
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function strArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function isStatus(value: unknown): value is CampaignStatus {
  return CAMPAIGN_STATUSES.includes(value as CampaignStatus);
}

export function parseCampaign(id: string, raw: Record<string, unknown>): Campaign | null {
  const name = str(raw.name);
  if (!name || !isStatus(raw.status)) return null;

  const budgetMode = raw.budgetMode === 'total' ? 'total' : 'daily';

  return {
    id,
    name,
    status: raw.status,
    brand: str(raw.brand),
    type: str(raw.type),
    updatedLabel: str(raw.updatedLabel),
    gradientIndex: typeof raw.gradientIndex === 'number' ? raw.gradientIndex : 0,
    description: str(raw.description),
    goal: str(raw.goal),
    start: str(raw.start),
    end: str(raw.end),
    budget: str(raw.budget),
    budgetMode,
    labels: strArray(raw.labels),
    audience: str(raw.audience),
    ageGroups: strArray(raw.ageGroups),
    regions: strArray(raw.regions),
    categories: strArray(raw.categories),
    focus: raw.focus === 'Service' ? 'Service' : 'Product',
  };
}
